import React, { useEffect, useState } from "react";
import { Doughnut, Pie } from "react-chartjs-2";
import "./index.scss";
import { useDispatch, useSelector } from "react-redux";
import transactionActions from "../../../redux/transactions/actions";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
} from "chart.js";
import { Select, Space } from "antd";
const { Option } = Select;

// Register necessary Chart.js components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale
);

// Function to generate a list of colors
const generateColors = () => [
  "#0072B2", // Strong Blue
  "#009E73", // Teal Green
  "#F0E442", // Bright Yellow
  "#56B4E9", // Sky Blue
  "#00AFBB", // Light Blue-Green
  "#E69F00", // Golden Yellow-Orange
  "#999999", // Medium Gray
  "#8DD3C7", // Soft Aqua
  "#FFFFB3", // Pale Yellow
  "#BEBADA", // Lavender
  "#80B1D3", // Pastel Blue
  "#B3DE69", // Light Lime Green
  "#CCEBC5", // Mint Green
  "#FFED6F", // Light Golden Yellow
  "#87CEEB", // Sky Blue
  "#4682B4", // Steel Blue
  "#A6D854", // Spring Green
  "#D9D9D9", // Soft Gray
  "#C5E384", // Yellowish Green
  "#6BAED6", // Muted Blue
];

export const PaymentModeChart = () => {
  const { getPieChartData } = transactionActions;
  const [selectionFilter, setSelectionFilter] = useState("Amount");
  const dispatch = useDispatch();
  const [timeRange, setTimeRange] = useState("allData");

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [],
      },
    ],
  });

  const { agentId } = useSelector((state) => state?.authReducer);
  const { pieChartData } = useSelector((state) => state?.transactionReducer);

  useEffect(() => {
    const raw = pieChartData?.data?.[timeRange] ?? [];

    // Flatten all entries across time into a single array
    const combinedData = raw.flatMap((entry) => entry.data);

    // Group by billerCategory
    const grouped = {};
    combinedData.forEach((item) => {
      const cat = item.billerCategory;
      if (!grouped[cat]) {
        grouped[cat] = {
          totalTransactions: 0,
          totalAmount: 0,
        };
      }
      grouped[cat].totalTransactions += item.totalTransactions;
      grouped[cat].totalAmount += item.totalAmount;
    });

    const categories = Object.keys(grouped);
    const totalAmount = categories.reduce(
      (sum, cat) => sum + grouped[cat].totalAmount,
      0
    );
    const totalTransactions = categories.reduce(
      (sum, cat) => sum + grouped[cat].totalTransactions,
      0
    );

    const amountData = categories.map(
      (cat) => (grouped[cat].totalAmount / totalAmount) * 100
    );
    const transactionData = categories.map(
      (cat) => (grouped[cat].totalTransactions / totalTransactions) * 100
    );

    const baseColors = generateColors(); // always same 20
    const sortedCategories = [...categories].sort();
    const colorMap = {};
    sortedCategories.forEach((cat, i) => {
      colorMap[cat] = baseColors[i];
    });
    const backgroundColor = categories.map(cat => colorMap[cat]);

    setChartData({
      labels: categories,
      datasets: [
        {
          totalAmount: categories.map((cat) => grouped[cat].totalAmount / 100),
          totalTransactions: categories.map(
            (cat) => grouped[cat].totalTransactions
          ),
          data: selectionFilter === "Amount" ? amountData : transactionData,
          backgroundColor: backgroundColor,
        },
      ],
    });
  }, [pieChartData?.data, selectionFilter, timeRange]);

  useEffect(() => {
    if (!agentId || agentId === "") {
      dispatch(getPieChartData("")); // Fetching data with empty agentId or default
    } else {
      dispatch(getPieChartData(agentId)); // Fetching data with agentId
    }
  }, [agentId, dispatch]);

  return (
    // <div className="d-flex flex-column gap-1">;
    <div className="d-flex flex-column gap-1 align-items-center justify-content-center">
      <h4 className="pie-h1">Payment Category Distribution</h4>
      <Space>
        {/* Filter by Amount or Transactions */}
        <Select
          value={selectionFilter}
          style={{ width: 130 }}
          onChange={(value) => {
            setSelectionFilter(value);
          }}
        >
          <Option value="Amount">Amount</Option>
          <Option value="Transactions">Transactions</Option>
        </Select>

        {/* Filter by Time Range */}
        <Select
          value={timeRange}
          style={{ width: 130 }}
          onChange={(value) => {
            setTimeRange(value);
          }}
        >
          <Option value="allData">All Data</Option>
          <Option value="daily">Daily</Option>
          <Option value="oneWeek">1 Week</Option>
          <Option value="oneMonth">1 Month</Option>
          <Option value="threeMonths">3 Months</Option>
          <Option value="sixMonths">6 Months</Option>
          <Option value="oneYear">1 Year</Option>
          <Option value="fiveYears">5 Years</Option>
        </Select>
      </Space>
      <div
        style={{
          width: "367px",
          height: "367px",
          marginTop: "20px",
          // overflow: "scroll",
        }}
      >
        {console.log(chartData, "chartData")}
        <Pie
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              tooltip: {
                callbacks: {
                  totalValue:
                    chartData?.datasets?.[0]?.totalTransactions?.reduce(
                      (total, value) => total + value,
                      0
                    ),
                  label: (tooltipItem, index) => {
                    const dataset = chartData?.datasets?.[0];
                    if (dataset) {
                      const totalAmount =
                        dataset.totalAmount?.[tooltipItem?.dataIndex];
                      const totalTransactions =
                        dataset.totalTransactions?.[tooltipItem?.dataIndex];
                      const label = tooltipItem?.label;
                      const rawValue = tooltipItem?.raw?.toFixed(2);
                      const totalValue =
                        selectionFilter === "Amount"
                          ? totalAmount
                          : totalTransactions;
                      const valueLabel =
                        selectionFilter === "Amount"
                          ? "Amount"
                          : "Transactions";
                      // Make sure to handle undefined values
                      return `${label} - ${rawValue}%, ${valueLabel}: ${
                        totalValue || "N/A"
                      }`;
                    }
                    return "";
                  },
                },
              },
              legend: {
                position: "bottom", // Place the legend on the right
                align: "start",
                labels: {
                  usePointStyle: true,
                  boxWidth: 12, // Set the width of the color boxes
                  padding: 10,
                  font: {
                    size: 12,
                  },
                  generateLabels: (chart) => {
                    const labels = chart.data.labels || [];
                    const datasets = chart.data.datasets || [];
                    return [...labels].map((label, index) => {
                      return {
                        text: `${label}`, // Add percentage next to label
                        fillStyle: datasets[0]?.backgroundColor[index],
                      };
                    });
                  },
                },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

// Transaction Status Chart Component
export const TransactionStatusChart = () => {
  const { pieChartData } = useSelector((state) => state.transactionReducer);
  // console.log(pieChartData , "kama")

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        data: [],
        backgroundColor: [], // Dynamically populated colors
      },
    ],
  });

  useEffect(() => {
    const statusStats = Array.isArray(pieChartData?.data?.statusStats)
      ? pieChartData?.data?.statusStats
      : [];

    if (statusStats.length > 0) {
      const statuses = statusStats.map((item) => item?.status ?? "Unknown");
      const percentages = statusStats.map((item) => item?.percentage ?? 0);

      const statusColors = statuses.map((status) => {
        switch (status) {
          case "Successful":
            return "#77B254"; // Green
          case "Pending":
            return "#ffc107"; // Yellow
          case "Failed":
            return "#EB5A3C"; // Red
          default:
            return "#007bff"; // Default color
        }
      });

      setChartData({
        labels: statuses,
        datasets: [
          {
            data: percentages,
            backgroundColor: statusColors,
          },
        ],
      });
    }
  }, [pieChartData?.data]);

  return (
    <div className="d-flex flex-column gap-1 align-items-center ">
      <h4 className="pie-h1">Transaction Rate</h4>
      <div style={{ width: "305px", height: "305px", marginTop: "58px" }}>
        <Doughnut
          data={chartData}
          options={{
            aspectRatio: 1,
            responsive: true,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (tooltipItem) =>
                    `${tooltipItem.label} - ${tooltipItem.raw.toFixed(2)}%`,
                },
              },
              legend: {
                position: "bottom", // Place the legend on the bottom
                labels: {
                  textAlign: "center",
                  usePointStyle: true,
                  boxWidth: 12, // Set the width of the color boxes
                  padding: 20, // Adjust the padding between the labels
                  font: {
                    size: 12, // Set the font size to the equivalent of Tailwind's text-md (16px)
                  },
                  generateLabels: (chart) => {
                    const labels = chart.data.labels || [];
                    const datasets = chart.data.datasets || [];
                    return labels.map((label, index) => {
                      return {
                        text: `${label}`, // Add percentage next to label
                        fillStyle: datasets[0]?.backgroundColor[index],
                      };
                    });
                  },
                },
                // Add margin-bottom for the entire legend container
                onClick: (e) => {
                  // Access the native event and stop propagation
                  if (e.native) {
                    e.native.stopPropagation();
                  }
                }, // Prevent interaction with the legend
              },
            },
          }}
        />
      </div>
    </div>
  );
};
